import { THEME_STORAGE_KEY } from '../theme/theme';

/**
 * localStorage key holding the build id that last wrote the client's persisted
 * state. Compared against the running build's id on boot; a mismatch means a new
 * version is live and any state an older build left behind should be dropped.
 */
export const BUILD_MARKER_KEY = 'kc-build';

/**
 * localStorage keys whose values survive a version reset. Only the theme mirror:
 * it keeps the pre-paint script from flashing the default theme on the reset load
 * (the .kiss.cat cookie is authoritative, but the mirror is the same-origin fast
 * path). The session is an httpOnly .kiss.cat cookie, not storage, so keeping the
 * user signed in needs nothing preserved here.
 */
const PRESERVED_LOCAL_KEYS = [THEME_STORAGE_KEY];

/**
 * Clear local + session storage when the running build differs from the one that
 * last wrote client state, keeping a small allow-list (the theme mirror) and
 * re-stamping the marker. Pure over injected Storage objects so it is deterministic
 * and unit-testable. Returns true when a reset happened.
 *
 * Why: tasks/auth persist the react-query cache to localStorage. A new build can add
 * a field to a cached entity; a component that reads it unguarded then crashes when
 * an OLD-shaped object rehydrates from a cache an earlier build wrote. TAS-89
 * white-screened the grouped board exactly this way (epic.breakdown missing on a
 * rehydrated epic). The react-query `buster` is meant to drop that cache; this is the
 * belt to its braces. On any build change we wipe ALL client storage so nothing
 * stale-shaped can outlive a deploy, while the auth cookie is left alone.
 *
 * A missing marker counts as a mismatch: the first load of the build that ships this
 * code finds no marker but may find a stale cache, and must clear it. A falsy buildId
 * is treated as unknown and never triggers a wipe.
 */
export function applyVersionReset(buildId: string, local: Storage, session: Storage): boolean {
	if (!buildId) {
		return false;
	}

	try {
		if (local.getItem(BUILD_MARKER_KEY) === buildId) {
			return false;
		}

		const preserved = new Map<string, string>();
		for (const key of PRESERVED_LOCAL_KEYS) {
			const value = local.getItem(key);
			if (value !== null) {
				preserved.set(key, value);
			}
		}

		local.clear();
		session.clear();

		for (const [key, value] of preserved) {
			local.setItem(key, value);
		}
		local.setItem(BUILD_MARKER_KEY, buildId);

		return true;
	} catch {
		// Storage disabled (privacy mode) or a quota error mid-clear: leave things as
		// they are. The react-query buster and defensive field access still apply.
		return false;
	}
}

/**
 * Browser entry point: run the version reset against window storage. No-op on the
 * server (SPA prerender) where there is no window. Call once, before the app hydrates
 * and the persisted react-query cache is restored (see each app's entry.client).
 */
export function resetStorageOnVersionChange(buildId: string): boolean {
	if (typeof window === 'undefined') {
		return false;
	}
	return applyVersionReset(buildId, window.localStorage, window.sessionStorage);
}
